/* eslint-disable camelcase */
import * as dayjs from 'dayjs';
import { MapUtils } from '../../utility/map-utils.js';
import { Country } from '../serializable/country.js';
import { PhoneNumber } from './phone-number.js';
import { TimeZoneRegion } from './time-zone-region.js';
import { DataModel, DataModelBuilder, isSameData, SerializationContext, SerializedData } from '../dto/dto.js';
import { CalendarBookingSettings } from './calendar-booking-settings.js';
import { BusinessHours } from './business-hours.js';
import { BusinessCategory } from './business-category.js';
import { PhotoCollection } from './photo-collection.js';
import { HealthAndSafetySettings } from './health-and-safety-settings.js';
import { VersionRange } from '../core/version-range.js';
import { SchedulingMode } from './scheduling-mode.js';
import { BusinessHoursComponent } from '../../components/business/business-hours-component.js';
import { BusinessRatingComponent } from '../../components/business/business-rating-component.js';
import { CalendarBookingSettingsComponent } from '../../components/business/calendar-booking-settings-component.js';
import { HealthAndSafetyComponent } from '../../components/business/health-and-safety-component.js';
import { ProfilePhotosComponent } from '../../components/misc/profile-photos-component.js';
import { ComponentList } from '../../components/core/component-list.js';
import { BusinessClassificationComponent } from '../../components/business/business-classification-component.js';
import { ContactComponent } from '../../components/misc/contact-component.js';
import { RichDescription } from './rich-description.js';
import { getLanguageOfCountry, Language } from '../serializable/language.js';
import { MapLocation } from './map-location.js';
import { BusinessLocationComponent } from '../../components/business/business-location-component.js';
import { PointOfInterestComponent } from '../../components/point-of-interest/point-of-interest-component.js';
import { BusinessPrivacySettingsComponent } from '../../components/business/business-privacy-settings-component.js';
import { BusinessPrivacySettings } from './business-privacy-settings.js';
import { BusinessPrivacyMode } from './business-privacy-mode.js';
import { LocalizedRichDescription } from './localized-rich-description.js';
import { LocalizedString } from './localized-string.js';
import { LocalizedRichDescriptionComponent } from '../../components/business/localized-rich-description-component.js';
import { BusinessModuleRef } from './business-module-ref.js';
import { BusinessModuleType } from './business-module-type.js';
import { ICopyWithBusinessRef } from '../../contracts/copy-with-business-ref.js';
import { AdvertProfileComponent } from '../../components/advert/advert-profile-component.js';
import { BusinessSettingsComponent } from '../../components/business/business-settings-component.js';
import { LastActivityComponent } from '../../components/business/last-activity-component.js';
import { PublishingStatusComponent } from '../../components/business/publishing-status-component.js';
import { ProfileElementCacheComponent } from '../../components/point-of-interest/profile-element-cache-component.js';
import { PoolBookingSettingsComponent } from '../../components/pool/pool-booking-settings-component.js';
import { BusinessSettings } from './business-settings.js';
import { ProfileElement } from './profile-element.js';
import { PublishingStatus } from './publishing-status.js';
import { MapViewComponent } from '../../components/point-of-interest/map-view-component.js';
import { Discoverable } from '../discoverable.js';
import { toEnumFromString, toEnumFromStringOrNull } from '../../serialization/enum-utils.js';

export class BusinessProfile extends Discoverable implements ICopyWithBusinessRef {
  static readonly TYPE_ID: string = 'c437a22af5cf40c39b2035ce58a31f12';
  static readonly SCHEMA_VERSION: number = 21;

  private constructor(
    public readonly uniqueId: string,
    public readonly creatorId: string,
    public readonly creationDate: dayjs.Dayjs,
    public readonly country: Country,
    public readonly language: Language,
    public readonly timezone: TimeZoneRegion,
    public readonly names: LocalizedString,
    public readonly businessRef: BusinessModuleRef,
    public readonly webRoute: string | null,
    public readonly components: ComponentList,
  ) {
    super();
  }

  get organizationId(): string {
    return this.businessRef.organizationId ?? this.creatorId;
  }

  get moduleType(): BusinessModuleType {
    return this.businessRef.moduleType ?? BusinessModuleType.Legacy;
  }

  get $typeId() {
    return BusinessProfile.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(19, BusinessProfile.SCHEMA_VERSION);
  }

  static create(opts: {
    uniqueId: string,
    creatorId: string,
    creationDate: dayjs.Dayjs,
    country: Country,
    language: Language,
    timezone: TimeZoneRegion,
    names: LocalizedString,
    businessRef: BusinessModuleRef,
    webRoute: string,
    components: ComponentList,
  }): BusinessProfile {
    return new BusinessProfile(
        opts.uniqueId,
        opts.creatorId,
        opts.creationDate,
        opts.country,
        opts.language,
        opts.timezone,
        opts.names,
        opts.businessRef,
        opts.webRoute,
        opts.components,
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): BusinessProfile {
    return new BusinessProfile(
        MapUtils.get<string>(map, 'uniqueId'),
        MapUtils.get<string>(map, 'creatorId'),
        context.decodeDate(MapUtils.get<number>(map, 'creationDate')),
        MapUtils.get<Country>(map, 'country'),
        MapUtils.get<Language>(map, 'language'),
        MapUtils.get<TimeZoneRegion>(map, 'timezone'),
        context.fromData<LocalizedString>(MapUtils.get<SerializedData>(map, 'names')),
        BusinessModuleRef.mustDecode(MapUtils.get<string>(map, 'businessRef')),
        MapUtils.getOrNull<string>(map, 'webRoute'),
        ComponentList.deserialize(
            context, MapUtils.getList<SerializedData>(
                map,
                'components',
                (data) => data as SerializedData,
            ),
        ),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'creatorId': this.creatorId,
      'creationDate': context.encodeDate(this.creationDate),
      'country': this.country,
      'language': this.language,
      'timezone': this.timezone,
      'names': context.toData(this.names),
      'businessRef': this.businessRef.encode(),
      'webRoute': this.webRoute,
      'components': this.components.serialize(context),
    };
  }

  clone(): this {
    return new BusinessProfile(
        this.uniqueId,
        this.creatorId,
        this.creationDate,
        this.country,
        this.language,
        this.timezone,
        this.names.clone(),
        this.businessRef.clone(),
        this.webRoute,
        this.components.clone(),
    ) as this;
  }

  isSame(other: BusinessProfile): boolean {
    if(other === this) {
      return true;
    }

    return other.uniqueId === this.uniqueId &&
      other.creatorId === this.creatorId &&
      other.country === this.country &&
      isSameData(other.names, this.names) &&
      other.creationDate === this.creationDate &&
      other.timezone === this.timezone &&
      other.language === this.language &&
      isSameData(other.businessRef, this.businessRef) &&
      other.webRoute === this.webRoute &&
      other.components.isSame(this.components);
  }

  copyWithBusinessRef(businessRef: BusinessModuleRef): this {
    return new BusinessProfile(
        this.uniqueId,
        this.creatorId,
        this.creationDate,
        this.country,
        this.language,
        this.timezone,
        this.names.clone(),
        businessRef.clone(),
        this.webRoute,
        this.components.copyWithBusinessRef(businessRef),
    ) as this;
  }

  // EXTENSION METHODS
  static verifyType(order: DataModel): boolean {
    return order instanceof BusinessProfile;
  }

  static assertType(order: DataModel): order is BusinessProfile {
    return order instanceof BusinessProfile;
  }

  getSchedulingMode(): SchedulingMode {
    if(this.components.hasComponentByTypeId(AdvertProfileComponent.TYPE_ID)) {
      return SchedulingMode.Advert;
    }

    if(this.components.hasComponentByTypeId(PoolBookingSettingsComponent.TYPE_ID)) {
      return SchedulingMode.Pool;
    }

    if(this.components.hasComponentByTypeId(CalendarBookingSettingsComponent.TYPE_ID)) {
      return SchedulingMode.Calendar;
    }

    return SchedulingMode.Unknown;
  }

  setRating(totalScore: number, numberOfRatings: number): void {
    const component = new BusinessRatingComponent(totalScore, numberOfRatings);

    this.components.addOrReplaceComponent(component);
  }

  setPublishingStatus(publishingStatus: PublishingStatus): void {
    const component = new PublishingStatusComponent(publishingStatus);

    this.components.addOrReplaceComponent(component);
  }

  setBusinessSettings(businessSettings: BusinessSettings): void {
    const component = new BusinessSettingsComponent(
        businessSettings.allowCustomBookingSettings,
        businessSettings.allowOpenReservations,
        businessSettings.localizationSettings
    );

    this.components.addOrReplaceComponent(component);
  }

  setProfileElementCache(profileElements: ProfileElement[]): void {
    const component = new ProfileElementCacheComponent(profileElements);

    this.components.addOrReplaceComponent(component);
  }

  setLastActivityTime(lastActivity: dayjs.Dayjs): void {
    const component = new LastActivityComponent(lastActivity);

    this.components.addOrReplaceComponent(component);
  }
}

export class BusinessProfileBuilder extends DataModelBuilder<BusinessProfile> {
  build(context: SerializationContext, map: SerializedData): BusinessProfile {
    return BusinessProfile.fromMap(context, map);
  }

  onBeforeDeserialization(context: SerializationContext, map: SerializedData): void {
    const language = toEnumFromStringOrNull(MapUtils.get<string>(map, 'language'), Language) ??
        getLanguageOfCountry(toEnumFromString(MapUtils.get<string>(map, 'country'), Country));

    context.storage.set('language', language);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);
    if(versionRange.contains(BusinessProfile.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(BusinessProfile.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version: number = versionRange.to;

    if(version < 2) {
      const classifications = MapUtils.getRaw(map, 'mainClassification') as string[];
      const indexOfHairSalon = classifications.indexOf('HairSalon');

      if(indexOfHairSalon >= 0) {
        classifications[indexOfHairSalon] = BusinessCategory.MensHaircut;
      }

      this.migrateEnumList(map, 'mainClassification', BusinessCategory);
      version = 2;
    }

    if(version < 3) {
      const workplacePhotos = context.fromData<PhotoCollection>(
        MapUtils.getRaw(map, 'workplacePhotos') as SerializedData,
      );
      const businessHours = context.fromData<BusinessHours>(
        MapUtils.getRaw(map, 'businessHours') as SerializedData,
      );
      const bookingSettings = context.fromData<CalendarBookingSettings>(
        MapUtils.getRaw(map, 'bookingSettings') as SerializedData,
      );
      const classification = MapUtils.getList<BusinessCategory>(
          map, 'mainClassification', (value) => value as BusinessCategory,
      );
      const healthAndSafetySettings = context.fromData<HealthAndSafetySettings>(
        MapUtils.getRaw(map, 'healthAndSafetySettings') as SerializedData,
      );
      const showPublicBusinessHours = MapUtils.getOrNull<boolean>(map, 'showPublicBusinessHours') ?? false;

      const components = new ComponentList([
        new ProfilePhotosComponent(workplacePhotos),
        new CalendarBookingSettingsComponent(bookingSettings),
        new BusinessClassificationComponent(classification),
        new HealthAndSafetyComponent(healthAndSafetySettings),
        BusinessHoursComponent.fromBusinessHours(businessHours, showPublicBusinessHours),
      ]);

      MapUtils.set(map, 'components', components.serialize(context));
      version = 3;
    }

    if(version < 4) {
      MapUtils.set(map, 'schedulingMode', SchedulingMode.Calendar);
      version = 4;
    }

    if(version < 5) {
      const components = ComponentList.deserialize(
          context, MapUtils.getList<SerializedData>(
              map,
              'components',
              (data) => data as SerializedData,
          ),
      );
      const totalScore = MapUtils.getOrNull<number>(map, 'totalScore') ?? 0;
      const numberOfRatings = MapUtils.getOrNull<number>(map, 'numberOfRatings') ?? 0;

      components.removeComponentByTypeId(BusinessRatingComponent.TYPE_ID);
      components.addComponent(new BusinessRatingComponent(totalScore, numberOfRatings));

      MapUtils.set(map, 'components', components.serialize(context));
      version = 5;
    }

    // In this version the [BusinessAppearanceSettingsComponent] was introduced.
    if(version < 6) {
      version = 6;
    }

    /**
     * In this version:
     *  - the [AdvertisingProfileComponent] was introduced.
     *  - migration safety was introduces for [schedulingMode]
     *  - the [BusinessContactComponent] was introduced.
     */
    if(version < 7) {
      const components = ComponentList.deserialize(
          context, MapUtils.getList<SerializedData>(
              map,
              'components',
              (data) => data as SerializedData,
          ),
      );
      const businessContactComponent = ContactComponent.empty();

      components.removeComponentByTypeId(ContactComponent.TYPE_ID);
      components.addComponent(businessContactComponent);

      MapUtils.set(map, 'components', components.serialize(context));
      version = 7;
    }

    // In this version the [richDescription] field was introduced
    if(version < 8) {
      MapUtils.set(map, 'richDescription', '');
      version = 8;
    }

    // In this version the [schedulingMode] field was removed
    if(version < 9) {
      version = 9;
    }

    // In this version the description was refactored into a separate data model
    if(version < 10) {
      const description = MapUtils.get<string>(map, 'description');
      const richDescription = MapUtils.get<string>(map, 'description');
      MapUtils.set(map, 'description', context.toData(new RichDescription(description ?? '', richDescription ?? '')));
      version = 10;
    }

    // In this version the [language] field was added
    if(version < 11) {
      const country = MapUtils.get<Country>(map, 'country');
      MapUtils.setIfNullOrUndefined(map, 'language', getLanguageOfCountry(country));
      version = 11;
    }

    // In this version the [isDemoBusiness] field was removed
    if(version < 12) {
      version = 12;
    }

    // In this version the [description], [location] and [phoneNumber] fields were moved to separate components
    if(version < 13) {
      const components = ComponentList.deserialize(
          context, MapUtils.getList<SerializedData>(
              map,
              'components',
              (data) => data as SerializedData,
          ),
      );
      const location = context.fromData<MapLocation>(MapUtils.getRaw(map, 'location') as SerializedData);
      const description = context.fromData<RichDescription>(MapUtils.getRaw(map, 'description') as SerializedData);
      const phoneNumber = PhoneNumber.decode(MapUtils.getOrNull<string>(map, 'phoneNumber'));
      const language = MapUtils.get<Language>(map, 'language');

      const contactComponent = components.mustFindComponentByTypeId<ContactComponent>(
          ContactComponent.TYPE_ID,
      );

      components.replaceComponent(
          contactComponent.copyWith({ phoneNumber: phoneNumber })
      );

      components.addComponent(LocalizedRichDescriptionComponent.fromRichDescription(
          LocalizedRichDescription.forLanguage(language, description)
      ));
      components.addComponent(new BusinessLocationComponent(location));

      MapUtils.set(map, 'components', components.serialize(context));

      version = 13;
    }

    // In this version the photo collection was removed from the PointOfInterestComponent
    if(version < 14) {
      const components = ComponentList.deserialize(
          context, MapUtils.getList<SerializedData>(
              map,
              'components',
              (data) => data as SerializedData,
          ),
      );
      const poi = components.findComponentByTypeId<PointOfInterestComponent>(PointOfInterestComponent.TYPE_ID);

      let photos = components.findComponentByTypeId<ProfilePhotosComponent>(ProfilePhotosComponent.TYPE_ID);

      if(photos == null) {
        const businessId = MapUtils.get<string>(map, 'uniqueId');
        const collectionId = 'workplacePhotos';

        photos = new ProfilePhotosComponent(
            new PhotoCollection(
                `business_profiles/${businessId}/${collectionId}`,
                `${businessId}.${collectionId}`,
                poi?.legacyProfilePhotos?.photos ?? [],
            ),
        );

        components.removeComponentByTypeId(ProfilePhotosComponent.TYPE_ID);
        components.addComponent(photos);
      }

      MapUtils.set(map, 'components', components.serialize(context));
      version = 14;
    }

    // In this version the business privacy mode has been moved to a new component
    if(version < 15) {
      const components = ComponentList.deserialize(
          context, MapUtils.getList<SerializedData>(
              map,
              'components',
              (data) => data as SerializedData,
          ),
      );
      const bookingSettingsComponent = components.findComponentByTypeId<CalendarBookingSettingsComponent>(
          CalendarBookingSettingsComponent.TYPE_ID,
      );

      if(bookingSettingsComponent !== null) {
        const privacyComponent = new BusinessPrivacySettingsComponent(
            new BusinessPrivacySettings(
                bookingSettingsComponent.settings.legacyPrivacyMode ?? BusinessPrivacyMode.Hidden,
            ),
        );

        components.addComponent(privacyComponent);
      }

      MapUtils.set(map, 'components', components.serialize(context));
      version = 15;
    }

    // In this version `name` of type [string] was replaced by `names` of type [LocalizedString].
    if(version < 16) {
      const language = MapUtils.get<Language>(map, 'language');
      const name = MapUtils.get<string>(map, 'name');
      map['names'] = context.toData(LocalizedString.forLanguage(language, name));

      version = 16;
    }

    // In this version the `organizationId` field was added

    if(version < 17) {
      MapUtils.setIfNullOrUndefined(map, 'organizationId', MapUtils.get(map, 'creatorId'));
      version = 17;
    }

    // In this version the `businessRef` field was added

    if(version < 18) {
      const uniqueId = MapUtils.get<string>(map, 'uniqueId');
      const organizationId = MapUtils.get<string>(map, 'organizationId');
      const businessRef = BusinessModuleRef.create(uniqueId, organizationId, BusinessModuleType.Legacy);

      MapUtils.set(map, 'businessRef', businessRef.encode());
      version = 18;
    }

    // In this version the `organizationId` field was removed

    if(version < 19) {
      version = 19;
    }

    // In this version [MapViewComponent] was added for POIs.
    if(version < 20) {
      const components = ComponentList.deserialize(
          context, MapUtils.getList<SerializedData>(map, 'components', (data) => data as SerializedData),
      );

      if(components.hasComponentByTypeId(PointOfInterestComponent.TYPE_ID)) {
        const locationComponent = components.findComponentByTypeId<BusinessLocationComponent>(
            BusinessLocationComponent.TYPE_ID
        );

        const mapViewComponent = components.findComponentByTypeId<MapViewComponent>(MapViewComponent.TYPE_ID) ??
            MapViewComponent.empty();

        mapViewComponent.coordinates ??= locationComponent?.location?.coordinates?.clone() ?? null;
        mapViewComponent.mapPreview ??= locationComponent?.location?.mapPreview?.clone() ?? null;

        components.addOrReplaceComponent(mapViewComponent);
      }

      map['components'] = components.serialize(context);
      version = 20;
    }

    // In this version the `webRoute` field of type String was added.
    if(version < 21) {
      version = 21;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
